import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { ChevronDown, Download, Loader2, Pencil, Plus, RotateCcw, Share2, Trash2 } from 'lucide-react';
import { ColumnToggle, type ColumnToggleGroup } from '../components/ColumnToggle';
import Pagination from '../components/Pagination';
import type { SelectOption } from '../components/MultiSelect';
import { getCurrentCalendarRange } from '../components/MarketingTimeRangeFilter';
import { PeriodRangePicker } from '../components/PeriodRangePicker';
import { TableLoadingCell } from '../components/TableLoadingOverlay';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import { acquisitionCostApi, type AcquisitionCostRow } from '../api/acquisitionCost';
import { useAppStore } from '../store/useAppStore';
import { useToastStore } from '../store/useToastStore';
import { useDownloadCenterStore } from '../store/useDownloadCenterStore';
import './AcquisitionCostDashboard.css';
import '../components/DataUpdateNote.css';

type DateGrain = 'day' | 'week' | 'month';
type DatePreset = 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'thisWeek' | 'thisMonth' | 'custom';

interface MetricColumn {
  key: string;
  groupKey: string;
  dataKey: string;
  label: string;
  section: string;
  className: 'platform' | 'onsiteNew' | 'onsite' | 'segment';
  format: 'money' | 'int' | 'percent';
}

interface MetricGroup {
  key: string;
  label: string;
  l1: string;
  l2: string;
  l3: string;
  flat: boolean;
  className: MetricColumn['className'];
  metrics: MetricColumn[];
}

interface SavedView {
  id: string;
  name: string;
  hiddenMetricKeys: string[];
}

interface SharedViewPayload {
  version: 1;
  view: Pick<SavedView, 'id' | 'name' | 'hiddenMetricKeys'>;
}

const SYSTEM_VIEW_ID = 'system-default';
const DEFAULT_PAGE_SIZE = 20;
const DATE_GRAINS: Array<{ value: DateGrain; label: string }> = [
  { value: 'day', label: '日' }, { value: 'week', label: '周' }, { value: 'month', label: '月' },
];
const DATE_PRESETS: Array<{ value: DatePreset; label: string }> = [
  { value: 'yesterday', label: '昨天' }, { value: '7d', label: '近 7 天' }, { value: '30d', label: '近 30 天' }, { value: '90d', label: '近 90 天' }, { value: 'thisWeek', label: '本周' }, { value: 'thisMonth', label: '本月' }, { value: 'custom', label: '自定义' },
];

type MetricSeed = readonly [suffix: string, label: string, format: MetricColumn['format']];

const METRIC_DATA_KEYS: Record<string, string> = {
  '账户花费': 'account_spend',
  '当月新客效果营销费用': 'current_mon_new_cus_performance_marketing_spend',
  '老客复购效果营销费用': 'old_cus_performance_marketing_spend',
  '支付用户数': 'pay_user_num',
  '首单用户数': 'first_user_num',
  '首单用户数_全站': 'first_user_num',
  '复购用户数': 'repeat_user_num',
  '首单用户占比': 'first_user_rate',
  '复购用户占比': 'repeat_user_rate',
  '首单cac': 'first_user_cac',
  '老客复购cac': 'old_user_rebuy_cac',
};

const NEW_FULL_METRICS: MetricSeed[] = [
  ['账户花费', '账户花费', 'money'], ['当月新客效果营销费用', '当月新客效果营销费用', 'money'], ['老客复购效果营销费用', '老客复购效果营销费用', 'money'],
  ['支付用户数', '支付用户数', 'int'], ['首单用户数', '首单用户数', 'int'], ['复购用户数', '复购用户数', 'int'],
  ['首单用户占比', '首单用户占比', 'percent'], ['首单cac', '首单CAC', 'money'], ['老客复购cac', '老客复购CAC', 'money'],
];
const NEW_SHORT_METRICS: MetricSeed[] = [
  ['账户花费', '账户花费', 'money'], ['当月新客效果营销费用', '当月新客效果营销费用', 'money'], ['支付用户数', '支付用户数', 'int'],
  ['首单用户数', '首单用户数', 'int'], ['首单用户占比', '首单用户占比', 'percent'], ['首单cac', '首单CAC', 'money'],
];
const NEW_TINY_METRICS: MetricSeed[] = [
  ['账户花费', '账户花费', 'money'], ['当月新客效果营销费用', '当月新客效果营销费用', 'money'], ['首单用户数', '首单用户数', 'int'],
  ['首单用户占比', '首单用户占比', 'percent'], ['首单cac', '首单CAC', 'money'],
];
const OLD_FULL_METRICS: MetricSeed[] = [
  ['账户花费', '账户花费', 'money'], ['老客复购效果营销费用', '老客复购效果营销费用', 'money'], ['复购用户数', '复购用户数', 'int'],
  ['复购用户占比', '复购用户占比', 'percent'], ['老客复购cac', '老客复购CAC', 'money'],
];
const OLD_SHORT_METRICS: MetricSeed[] = [
  ['账户花费', '账户花费', 'money'], ['老客复购效果营销费用', '老客复购效果营销费用', 'money'], ['复购用户数', '复购用户数', 'int'], ['老客复购cac', '老客复购CAC', 'money'],
];
const SITE_METRICS: MetricSeed[] = [
  ['账户花费', '账户花费', 'money'], ['支付用户数', '支付用户数', 'int'], ['当月新客效果营销费用', '当月新客效果营销费用', 'money'],
  ['老客复购效果营销费用', '老客复购效果营销费用', 'money'], ['首单用户数_全站', '首单用户数（全站）', 'int'], ['复购用户数', '复购用户数', 'int'],
  ['首单用户占比', '首单用户占比', 'percent'], ['首单cac', '首单CAC', 'money'], ['老客复购cac', '老客复购CAC', 'money'],
];

function createGroup(key: string, label: string, l1: string, l2: string, l3: string, className: MetricColumn['className'], flat: boolean, seeds: MetricSeed[]): MetricGroup {
  const section = [l1, l2, l3].filter(Boolean).join(' / ');
  return { key, label, l1, l2, l3, className, flat, metrics: seeds.map(([suffix, metricLabel, format]) => ({ key: `${key}_${suffix}`, groupKey: key, dataKey: METRIC_DATA_KEYS[suffix] ?? suffix, label: metricLabel, section, className, format })) };
}

// 与原型的固定渠道层级一一对应，展示列不受筛选条件影响。
const METRIC_GROUPS: MetricGroup[] = [
  createGroup('site', '整体', '整体', '', '', 'platform', true, SITE_METRICS),
  createGroup('paid_total', 'Total', '付费广告', 'Total', '', 'onsiteNew', false, NEW_FULL_METRICS),
  createGroup('fb', 'fb_ads', '付费广告', 'fb_ads', 'fb_ads', 'onsiteNew', false, NEW_FULL_METRICS),
  createGroup('gg_total', 'Total', '付费广告', 'google_ads', 'Total', 'onsiteNew', false, NEW_FULL_METRICS),
  createGroup('gg_brand', 'google_ads_brand', '付费广告', 'google_ads', 'google_ads_brand', 'onsiteNew', false, NEW_SHORT_METRICS),
  createGroup('gg_pmax', 'google_ads_pmax', '付费广告', 'google_ads', 'google_ads_pmax', 'onsiteNew', false, NEW_SHORT_METRICS),
  createGroup('gg_search', 'google_ads_paidsearch', '付费广告', 'google_ads', 'google_ads_paidsearch', 'onsiteNew', false, NEW_FULL_METRICS),
  createGroup('bing', 'bing_ads', '付费广告', 'bing_ads', 'bing_ads', 'onsiteNew', false, NEW_TINY_METRICS),
  createGroup('snap', 'snapchat_ads', '付费广告', 'snapchat_ads', 'snapchat_ads', 'onsiteNew', false, NEW_TINY_METRICS),
  createGroup('rtb', 'rtb_ads', '付费广告', 'rtb_ads', 'rtb_ads', 'onsiteNew', false, OLD_FULL_METRICS),
  createGroup('apple', 'apple_ads', '付费广告', 'apple_ads', 'apple_ads', 'onsiteNew', false, OLD_SHORT_METRICS),
  createGroup('user', '用户营销', '用户营销', '', '', 'onsite', true, OLD_FULL_METRICS),
  createGroup('aff', 'Affiliate', 'Affiliate', '', '', 'segment', true, OLD_FULL_METRICS),
];

function ymd(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function today() {
  return ymd(new Date());
}

function yesterday() {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - 1);
  return ymd(date);
}

function presetRange(preset: Exclude<DatePreset, 'custom'>, grain: DateGrain) {
  if (preset === 'thisWeek' || preset === 'thisMonth') {
    const range = getCurrentCalendarRange(preset, today());
    return { start: periodStart(range.start, grain), end: periodStart(range.end, grain) };
  }
	if (preset === 'today' || preset === 'yesterday') {
		const value = periodStart(preset === 'today' ? today() : yesterday(), grain);
		return { start: value, end: value };
	}
	const days = preset === '7d' ? 6 : preset === '30d' ? 29 : 89;
  const end = new Date(`${yesterday()}T00:00:00Z`);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - days);
  return { start: periodStart(ymd(start), grain), end: periodStart(ymd(end), grain) };
}

function periodStart(value: string, grain: DateGrain) {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  if (grain === 'week') {
    const weekday = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() - weekday + 1);
  } else if (grain === 'month') date.setUTCDate(1);
  return ymd(date);
}

function formatPeriod(value: string, grain: DateGrain) {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  const year = date.getUTCFullYear();
  if (grain === 'week') {
    const end = new Date(date);
    end.setUTCDate(end.getUTCDate() + 6);
    const first = new Date(Date.UTC(year, 0, 1));
    const week = Math.ceil((((date.getTime() - first.getTime()) / 86400000) + first.getUTCDay() + 1) / 7);
    return `${year}年第${week}周(${ymd(date)}至${ymd(end)})`;
  }
  if (grain === 'month') return `${year}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
  return value;
}

function buildPeriodOptions(grain: DateGrain): SelectOption[] {
  const result: SelectOption[] = [];
  const cursor = new Date(`${periodStart('2025-01-01', grain)}T00:00:00Z`);
  const max = new Date(`${today()}T00:00:00Z`);
  while (cursor <= max) {
    const value = periodStart(ymd(cursor), grain);
    if (!result.some(item => item.value === value)) result.push({ value, label: formatPeriod(value, grain) });
    if (grain === 'month') cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    else if (grain === 'day') cursor.setUTCDate(cursor.getUTCDate() + 1);
    else cursor.setUTCDate(cursor.getUTCDate() + 7);
  }
  return result;
}

function formatMetric(value: number | undefined, format: MetricColumn['format']) {
  const number = Number(value ?? 0);
  if (format === 'percent') return `${(number * 100).toFixed(2)}%`;
  if (format === 'int') return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(number);
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(number);
}

function exportFileName() {
  const now = new Date();
  const timestamp = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('') + [String(now.getHours()).padStart(2, '0'), String(now.getMinutes()).padStart(2, '0'), String(now.getSeconds()).padStart(2, '0')].join('');
  const suffix = String(Math.floor(Math.random() * 10_000)).padStart(4, '0');
  return `获客成本_${timestamp}${suffix}.xlsx`;
}

function displayTime(value: string, grain: DateGrain) {
  return formatPeriod(value, grain);
}

function metricLeaves(group: MetricGroup): ColumnToggleGroup[] {
  return group.metrics.map(metric => ({ key: `metric:${metric.key}`, label: metric.label, columnKeys: [metric.key] }));
}

function acquisitionColumnHierarchy(groups: MetricGroup[]): ColumnToggleGroup[] {
  const roots: ColumnToggleGroup[] = [];
  const sections: Array<{ l1: string; groups: MetricGroup[] }> = [];
  groups.forEach(group => {
    const last = sections[sections.length - 1];
    if (last && last.l1 === group.l1) last.groups.push(group);
    else sections.push({ l1: group.l1, groups: [group] });
  });
  sections.forEach(section => {
    if (section.l1 !== '付费广告') {
      roots.push({ key: `root:${section.l1}`, label: section.l1, children: section.groups.flatMap(metricLeaves) });
      return;
    }
    const levelTwo: Array<{ label: string; groups: MetricGroup[] }> = [];
    section.groups.forEach(group => {
      const label = group.l2 || group.label;
      const last = levelTwo[levelTwo.length - 1];
      if (last && last.label === label) last.groups.push(group); else levelTwo.push({ label, groups: [group] });
    });
    roots.push({
      key: 'root:付费广告', label: '付费广告', defaultExpanded: true,
      children: levelTwo.map(block => {
        const only = block.groups.length === 1 ? block.groups[0] : null;
        const mergeLevel = Boolean(only && (!only.l3 || only.l3 === only.l2));
        return mergeLevel && only
          ? { key: `group:${only.key}`, label: only.label, children: metricLeaves(only) }
          : { key: `channel:${block.label}`, label: block.label, defaultExpanded: block.label === 'google_ads', children: block.groups.map(group => ({ key: `group:${group.key}`, label: group.l3 || group.label, children: metricLeaves(group) })) };
      }),
    });
  });
  return roots;
}

function viewStorageKey(brandId: string, siteId: string) {
  return `acquisition-cost:views:${brandId}:${siteId}`;
}

function lastViewStorageKey(brandId: string, siteId: string) {
  return `acquisition-cost:last-view:${brandId}:${siteId}`;
}

function parseSavedViews(value: string | null): SavedView[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is SavedView => Boolean(item && typeof item === 'object' && typeof (item as SavedView).id === 'string' && typeof (item as SavedView).name === 'string' && Array.isArray((item as SavedView).hiddenMetricKeys)))
      .map(item => ({ ...item, hiddenMetricKeys: item.hiddenMetricKeys.filter(key => typeof key === 'string') }));
  } catch { return []; }
}

function parseSharedView(value: string | null): SharedViewPayload | null {
  if (!value || value.length > 12_000) return null;
  try {
    const payload: unknown = JSON.parse(value);
    if (!payload || typeof payload !== 'object') return null;
    const shared = payload as SharedViewPayload;
    if (shared.version !== 1 || !shared.view || typeof shared.view.name !== 'string' || !Array.isArray(shared.view.hiddenMetricKeys)) return null;
    return { version: 1, view: { id: typeof shared.view.id === 'string' ? shared.view.id : '', name: shared.view.name.trim().slice(0, 30) || '共享视图', hiddenMetricKeys: shared.view.hiddenMetricKeys.filter(key => typeof key === 'string') } };
  } catch { return null; }
}

function uniqueViewName(views: SavedView[], name: string) {
  if (!views.some(view => view.name === name)) return name;
  const base = `${name}（共享）`.slice(0, 30);
  if (!views.some(view => view.name === base)) return base;
  for (let index = 2; index < 100; index += 1) {
    const candidate = `${base.slice(0, Math.max(1, 28 - String(index).length))} ${index}`;
    if (!views.some(view => view.name === candidate)) return candidate;
  }
  return `${base.slice(0, 22)} ${Date.now().toString().slice(-5)}`;
}

function TableViewMenu({ currentViewID, views, onSelect, onSaveAs, onRename, onShare, onDelete }: {
  currentViewID: string;
  views: SavedView[];
  onSelect: (id: string) => void;
  onSaveAs: () => void;
  onRename: (view: SavedView) => void;
  onShare: (view: SavedView) => void;
  onDelete: (view: SavedView) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = currentViewID === SYSTEM_VIEW_ID ? '系统默认' : views.find(item => item.id === currentViewID)?.name ?? '系统默认';
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => { if (!ref.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return <div className="acquisition-view-menu" ref={ref}>
    <button type="button" className={`acquisition-view-trigger${open ? ' open' : ''}`} onClick={() => setOpen(value => !value)}><span>{selected}</span><ChevronDown size={14} /></button>
    {open && <div className="acquisition-view-dropdown" role="menu">
      <button type="button" className={`acquisition-view-option${currentViewID === SYSTEM_VIEW_ID ? ' selected' : ''}`} onClick={() => { onSelect(SYSTEM_VIEW_ID); setOpen(false); }}><span>系统默认</span>{currentViewID === SYSTEM_VIEW_ID && <b>✓</b>}</button>
      {views.map(view => <div className={`acquisition-view-option acquisition-view-saved${currentViewID === view.id ? ' selected' : ''}`} key={view.id}>
        <button type="button" className="acquisition-view-name" onClick={() => { onSelect(view.id); setOpen(false); }} title={view.name}>{view.name}</button>
        <span className="acquisition-view-actions"><button type="button" title="重命名" onClick={() => { onRename(view); setOpen(false); }}><Pencil size={12} /></button><button type="button" title="分享" onClick={() => { onShare(view); setOpen(false); }}><Share2 size={12} /></button><button type="button" title="删除" className="danger" onClick={() => { onDelete(view); setOpen(false); }}><Trash2 size={12} /></button></span>
      </div>)}
      <div className="acquisition-view-dropdown-footer"><button type="button" onClick={() => { onSaveAs(); setOpen(false); }}><Plus size={13} />另存为视图</button></div>
    </div>}
  </div>;
}

export default function AcquisitionCostDashboard() {
  const showToast = useToastStore(state => state.show);
  const enqueueReportExport = useDownloadCenterStore(state => state.enqueueReportExport);
  const brandId = useAppStore(state => state.brandId);
  const siteId = useAppStore(state => state.siteId);
  const [dateGrain, setDateGrain] = useState<DateGrain>('day');
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState('2026-08-08');
  const [customEnd, setCustomEnd] = useState('2026-08-14');
  const [rows, setRows] = useState<AcquisitionCostRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [appliedDateGrain, setAppliedDateGrain] = useState<DateGrain>('day');
  const [appliedRange, setAppliedRange] = useState<{ start: string; end: string } | null>(null);
  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set());
  const [views, setViews] = useState<SavedView[]>([]);
  const [currentViewID, setCurrentViewID] = useState(SYSTEM_VIEW_ID);
  const [viewDialog, setViewDialog] = useState<{ view?: SavedView; value: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isTableDragging, setIsTableDragging] = useState(false);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const tableDragRef = useRef<{ pointerID: number | null; startX: number; scrollLeft: number }>({ pointerID: null, startX: 0, scrollLeft: 0 });
  const sharedViewHandledRef = useRef<string | null>(null);
  const initialQueryKeyRef = useRef('');

  const storageKey = useMemo(() => viewStorageKey(brandId || 'vooglam', siteId || 'us'), [brandId, siteId]);
  const lastStorageKey = useMemo(() => lastViewStorageKey(brandId || 'vooglam', siteId || 'us'), [brandId, siteId]);

  useEffect(() => {
    const stored = parseSavedViews(localStorage.getItem(storageKey));
    const selectedID = localStorage.getItem(lastStorageKey) ?? SYSTEM_VIEW_ID;
    const selected = stored.find(view => view.id === selectedID);
    setViews(stored);
    setCurrentViewID(selected?.id ?? SYSTEM_VIEW_ID);
    setHiddenMetrics(new Set(selected?.hiddenMetricKeys ?? []));
  }, [lastStorageKey, storageKey]);

  useEffect(() => {
    const rawPayload = new URLSearchParams(window.location.search).get('shared_view');
    if (!rawPayload || sharedViewHandledRef.current === rawPayload) return;
    sharedViewHandledRef.current = rawPayload;
    const payload = parseSharedView(rawPayload);
    const url = new URL(window.location.href);
    url.searchParams.delete('shared_view');
    window.history.replaceState({}, '', url.toString());
    if (!payload) {
      showToast('分享链接无效或已损坏', 'error');
      return;
    }
    const stored = parseSavedViews(localStorage.getItem(storageKey));
    const view: SavedView = { id: `shared-${Date.now()}`, name: uniqueViewName(stored, payload.view.name), hiddenMetricKeys: payload.view.hiddenMetricKeys };
    const nextViews = [...stored, view];
    localStorage.setItem(storageKey, JSON.stringify(nextViews));
    localStorage.setItem(lastStorageKey, view.id);
    setViews(nextViews);
    setCurrentViewID(view.id);
    setHiddenMetrics(new Set(view.hiddenMetricKeys));
    showToast(`已保存共享视图「${view.name}」`, 'success');
  }, [lastStorageKey, showToast, storageKey]);

  const periodOptions = useMemo(() => buildPeriodOptions(dateGrain), [dateGrain]);
  const scopedMetricGroups = METRIC_GROUPS;
  const scopedMetrics = useMemo(() => scopedMetricGroups.flatMap(group => group.metrics), [scopedMetricGroups]);
  const columnHierarchy = useMemo(() => acquisitionColumnHierarchy(scopedMetricGroups), [scopedMetricGroups]);
  const visibleMetrics = useMemo(() => scopedMetrics.filter(metric => !hiddenMetrics.has(metric.key)), [scopedMetrics, hiddenMetrics]);
  const visibleMetricGroups = useMemo(() => scopedMetricGroups.map(group => ({ ...group, metrics: group.metrics.filter(metric => !hiddenMetrics.has(metric.key)) })).filter(group => group.metrics.length > 0), [scopedMetricGroups, hiddenMetrics]);
  const metricSections = useMemo(() => {
    const sections: Array<{ l1: string; className: MetricColumn['className']; flat: boolean; groups: typeof visibleMetricGroups }> = [];
    visibleMetricGroups.forEach(group => {
      const last = sections[sections.length - 1];
      if (last && last.l1 === group.l1) last.groups.push(group);
      else sections.push({ l1: group.l1, className: group.className, flat: group.flat, groups: [group] });
    });
    return sections;
  }, [visibleMetricGroups]);
  const hasChannelLevels = metricSections.some(section => !section.flat);
  const channelHeaderBlocks = useMemo(() => metricSections.filter(section => !section.flat).flatMap(section => {
    const blocks: Array<{ label: string; className: MetricColumn['className']; metrics: MetricColumn[]; children: typeof visibleMetricGroups }> = [];
    section.groups.forEach(group => {
      const label = group.l2 || group.label;
      const last = blocks[blocks.length - 1];
      if (last && last.label === label) { last.metrics.push(...group.metrics); last.children.push(group); }
      else blocks.push({ label, className: group.className, metrics: [...group.metrics], children: [group] });
    });
    return blocks;
  }), [metricSections, visibleMetricGroups]);
  const currentView = currentViewID === SYSTEM_VIEW_ID ? undefined : views.find(view => view.id === currentViewID);

  const selectGrain = (value: DateGrain) => {
    setDateGrain(value);
    setCustomStart(current => periodStart(current, value));
    setCustomEnd(current => periodStart(current, value));
  };
  const persistViews = (next: SavedView[], activeID = currentViewID) => { setViews(next); setCurrentViewID(activeID); localStorage.setItem(storageKey, JSON.stringify(next)); localStorage.setItem(lastStorageKey, activeID); };
  const chooseView = (id: string) => {
    setCurrentViewID(id);
    const view = views.find(item => item.id === id);
    setHiddenMetrics(new Set(view?.hiddenMetricKeys ?? []));
    localStorage.setItem(lastStorageKey, id);
  };
  const saveView = () => {
    const name = viewDialog?.value.trim();
    if (!name) { showToast('请输入视图名称', 'error'); return; }
    if (views.some(view => view.name === name && view.id !== viewDialog?.view?.id)) { showToast('已存在同名视图', 'error'); return; }
    const nextView: SavedView = viewDialog?.view ? { ...viewDialog.view, name, hiddenMetricKeys: [...hiddenMetrics] } : { id: `view-${Date.now()}`, name, hiddenMetricKeys: [...hiddenMetrics] };
    const next = viewDialog?.view ? views.map(view => view.id === nextView.id ? nextView : view) : [...views, nextView];
    persistViews(next, nextView.id);
    setViewDialog(null);
    showToast('视图保存成功', 'success');
  };
  const shareView = async (view: SavedView) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('shared_view');
    const payload: SharedViewPayload = { version: 1, view: { id: view.id, name: view.name, hiddenMetricKeys: view.hiddenMetricKeys } };
    url.searchParams.set('shared_view', JSON.stringify(payload));
    try {
      await navigator.clipboard.writeText(url.toString());
      showToast('复制链接成功，可进行分享', 'success');
    } catch {
      const input = document.createElement('textarea');
      input.value = url.toString();
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(input);
      showToast(copied ? '复制链接成功，可进行分享' : '复制链接失败，请检查浏览器权限', copied ? 'success' : 'error');
    }
  };
  const removeView = (view: SavedView) => {
    const next = views.filter(item => item.id !== view.id);
    persistViews(next, currentViewID === view.id ? SYSTEM_VIEW_ID : currentViewID);
    if (currentViewID === view.id) setHiddenMetrics(new Set());
    showToast('视图已删除', 'success');
  };
  const runQuery = async (nextPage = 1, nextPageSize = pageSize) => {
    if (!brandId || !siteId) return;
    const range = datePreset === 'custom' ? { start: customStart, end: customEnd } : presetRange(datePreset, dateGrain);
    setLoading(true);
    try {
      const response = await acquisitionCostApi.getMonitor({ brand_id: brandId, site_id: siteId, start_date: range.start, end_date: range.end, date_grain: dateGrain, page: nextPage, size: nextPageSize });
      setRows(response.data.list);
      setPage(response.data.page ?? nextPage);
      setTotal(response.data.total ?? 0);
      setTotalPage(response.data.total_page ?? 1);
      setAppliedDateGrain(dateGrain);
      setAppliedRange(range);
    } catch (error) {
      setRows([]);
      setPage(1);
      setTotal(0);
      setTotalPage(1);
      showToast(error instanceof Error ? error.message : '获客成本查询失败', 'error');
    } finally {
      setLoading(false);
    }
  };
  // 仅首次进入或切换品牌/站点时查询；筛选项变更必须由查询按钮提交。
  useEffect(() => {
    if (!brandId || !siteId) return;
    const key = `${brandId}:${siteId}`;
    if (initialQueryKeyRef.current === key) return;
    initialQueryKeyRef.current = key;
    window.setTimeout(() => { void runQuery(1); }, 0);
  }, [brandId, siteId]); // eslint-disable-line react-hooks/exhaustive-deps
  useMarketingViewSection('page', {
    label: '页面筛选', state: { dateGrain, datePreset, customStart, customEnd, hiddenMetrics: [...hiddenMetrics] },
    restore: value => { setDateGrain(value.dateGrain); setDatePreset(value.datePreset); setCustomStart(value.customStart); setCustomEnd(value.customEnd); setHiddenMetrics(new Set(value.hiddenMetrics)); },
    loading: loading, query: () => { void runQuery(1); },
    resolve: value => { if (value.datePreset !== 'today') return value; const range = presetRange('today', value.dateGrain); return { ...value, datePreset: 'custom' as const, customStart: range.start, customEnd: range.end }; },
  });

  const reset = () => {
    setDateGrain('day');
    setDatePreset('7d');
    const range = presetRange('7d', 'day');
    setCustomStart(range.start);
    setCustomEnd(range.end);
    void runQuery(1);
  };
  const exportTable = async () => {
    if (!brandId || !siteId || !appliedRange || exporting) return;
    setExporting(true);
    try {
      await enqueueReportExport({
        report_key: 'acquisition_cost',
        title: '获客成本',
        file_name: exportFileName(),
        brand_id: brandId,
        site_id: siteId,
        params: {
          start_date: appliedRange.start,
          end_date: appliedRange.end,
          date_grain: appliedDateGrain,
        },
        columns: visibleMetrics.map(metric => ({ key: `${metric.groupKey}:${metric.dataKey}`, label: metric.label })),
      });
      showToast('导出任务已提交，请到右上角下载中心查看进度和下载文件', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : '获客成本导出失败', 'error');
    } finally {
      setExporting(false);
    }
  };
  const startTableDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || event.target instanceof Element && event.target.closest('button, input, a')) return;
    const container = event.currentTarget;
    tableDragRef.current = { pointerID: event.pointerId, startX: event.clientX, scrollLeft: container.scrollLeft };
    container.setPointerCapture(event.pointerId);
    setIsTableDragging(true);
  };
  const moveTableDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = tableDragRef.current;
    if (drag.pointerID !== event.pointerId) return;
    event.currentTarget.scrollLeft = drag.scrollLeft - (event.clientX - drag.startX);
  };
  const endTableDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (tableDragRef.current.pointerID !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    tableDragRef.current.pointerID = null;
    setIsTableDragging(false);
  };

  return <div className="page-section visible acquisition-cost-page">
    <aside className="data-update-note" aria-labelledby="acquisition-data-note-title">
      <h2 id="acquisition-data-note-title">数据更新说明</h2>
      <div className="data-update-note-content">
        <p>1、用户营销、Affiliate 的花费数据依赖业务同学每天 10 点前在钉钉文档中更新前一天的花费数据，这两部分的花费以及花费相关数据（如CAC）会在中午 13 点以后产出（如果当天 10 点前没有更新前一天的花费数据，这些数据当天就不会更新）</p>
        <p>2、其余数据会在每天 8 点前在看板中完成更新</p>
      </div>
    </aside>
    <div className="dashboard-sticky-filters">
      <section className="data-filter-card acquisition-filter-card">
        <div className="data-filter-row time acquisition-time-row">
          <span className="data-filter-inline-label">时间粒度</span>
          <div className="page-tabs" role="group" aria-label="时间粒度">{DATE_GRAINS.map(item => <button type="button" key={item.value} className={`page-tab${dateGrain === item.value ? ' active' : ''}`} onClick={() => selectGrain(item.value)}>{item.label}</button>)}</div>
          <span className="data-filter-inline-label">时间范围</span>
          <div className="page-tabs" role="group" aria-label="时间范围">{DATE_PRESETS.map(item => <button type="button" key={item.value} className={`page-tab${datePreset === item.value ? ' active' : ''}`} onClick={() => setDatePreset(item.value)}>{item.label}</button>)}</div>
          {datePreset === 'custom' && <PeriodRangePicker grain={dateGrain} start={customStart} end={customEnd} max={today()} options={periodOptions} formatValue={value => formatPeriod(value, dateGrain)} normalizeValue={value => periodStart(value, dateGrain)} onChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }} />}
          <div className="acquisition-query-actions"><button type="button" className="btn btn-outline btn-sm" onClick={reset}><RotateCcw size={13} />重置</button><button type="button" className="btn btn-primary btn-sm" onClick={() => void runQuery(1)} disabled={loading}>{loading ? <><Loader2 size={14} className="acquisition-spinner" />查询中…</> : '查询'}</button></div>
        </div>
      </section>
    </div>

    <section className="acquisition-panel">
      <div className="acquisition-panel-head">
        <div><h1>获客成本</h1><p>按时间查看整站与分渠道获客成本</p></div>
        <div className="acquisition-panel-tools">
          <button type="button" className="btn btn-outline btn-sm" onClick={() => void exportTable()} disabled={exporting || !appliedRange}>{exporting ? <><Loader2 size={14} className="acquisition-spinner" />提交中…</> : <><Download size={14} />导出</>}</button>
          <span className="acquisition-view-label">表格视图</span>
          <TableViewMenu currentViewID={currentViewID} views={views} onSelect={chooseView} onSaveAs={() => setViewDialog({ value: '' })} onRename={view => setViewDialog({ view, value: view.name })} onShare={view => void shareView(view)} onDelete={removeView} />
          <ColumnToggle cols={scopedMetrics.map(metric => ({ ...metric, section: metric.section }))} hierarchy={columnHierarchy} hiddenKeys={hiddenMetrics} onToggle={key => setHiddenMetrics(current => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; })} onReset={() => setHiddenMetrics(new Set())} viewConfig={{ currentViewName: currentView?.name ?? '系统默认', isSystemView: !currentView, onSave: () => setViewDialog(currentView ? { view: currentView, value: currentView.name } : { value: '' }), onSaveAs: () => setViewDialog({ value: '' }) }} />
        </div>
      </div>
      <div ref={tableWrapRef} className={`acquisition-table-wrap${isTableDragging ? ' is-dragging' : ''}`} onPointerDown={startTableDrag} onPointerMove={moveTableDrag} onPointerUp={endTableDrag} onPointerCancel={endTableDrag}>
        <table className="acquisition-table">
          <thead>
            <tr className="acquisition-group-row">
              <th rowSpan={hasChannelLevels ? 4 : 2} className="acquisition-time-head">时间</th>
              {metricSections.map(section => <th key={section.l1} colSpan={section.groups.reduce((total, group) => total + group.metrics.length, 0)} rowSpan={section.flat ? (hasChannelLevels ? 3 : 1) : 1} className={`acquisition-group-head ${section.className}`}><span>{section.l1}</span></th>)}
            </tr>
            {hasChannelLevels && <tr className="acquisition-second-row">{channelHeaderBlocks.map(block => {
              const only = block.children.length === 1 ? block.children[0] : null;
              const merge = Boolean(only && (!only.l3 || only.l3 === only.l2));
              return <th key={`${block.label}-${block.metrics[0]?.key ?? ''}`} colSpan={block.metrics.length} rowSpan={merge ? 2 : 1} className={`acquisition-channel-head ${block.className}`}>{block.label}</th>;
            })}</tr>}
            {hasChannelLevels && <tr className="acquisition-third-row">{channelHeaderBlocks.flatMap(block => block.children.length === 1 && (!block.children[0].l3 || block.children[0].l3 === block.children[0].l2) ? [] : block.children.map(group => <th key={group.key} colSpan={group.metrics.length} className={`acquisition-channel-head ${group.className}`}>{group.l3 || group.label}</th>))}</tr>}
            <tr className="acquisition-metric-row">{visibleMetricGroups.flatMap(group => group.metrics.map((metric, index) => <th key={metric.key} className={`${metric.className}${index === group.metrics.length - 1 ? ' group-end' : ''}`}><MetricDescriptionTooltip label={metric.label} /></th>))}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="acquisition-placeholder-row acquisition-initial-loading-row"><td colSpan={visibleMetrics.length + 1}><TableLoadingCell /></td></tr>
            ) : rows.length > 0 ? (
              rows.map((row, index) => <tr key={row.time} className={index === 0 ? 'acquisition-total-row' : ''}><td className="acquisition-time-cell">{displayTime(row.time, appliedDateGrain)}</td>{visibleMetricGroups.flatMap(group => group.metrics.map((metric, metricIndex) => <td key={metric.key} className={`${metric.className}${metricIndex === group.metrics.length - 1 ? ' group-end' : ''}`}>{formatMetric(row.metrics[group.key]?.[metric.dataKey], metric.format)}</td>))}</tr>)
            ) : <tr><td className="acquisition-empty" colSpan={visibleMetrics.length + 1}>暂无数据</td></tr>}
          </tbody>
        </table>
      </div>
      {total > 0 && <Pagination className="acquisition-pagination" page={page} totalPages={Math.max(1, totalPage)} onChange={nextPage => void runQuery(nextPage)} total={`共 ${total} 条`} pageSize={pageSize} pageSizeOptions={[10, 20, 50, 100]} onPageSizeChange={nextPageSize => { setPageSize(nextPageSize); setPage(1); void runQuery(1, nextPageSize); }} disabled={loading} />}
    </section>

    {viewDialog && <div className="modal-overlay open" onClick={() => setViewDialog(null)}><div className="modal acquisition-view-modal" role="dialog" aria-modal="true" onClick={event => event.stopPropagation()}><div className="modal-header"><h3>{viewDialog.view ? '修改视图名称' : '另存为视图'}</h3><button type="button" className="modal-close" aria-label="关闭" onClick={() => setViewDialog(null)}>×</button></div><div className="modal-body acquisition-view-modal-body"><label className="form-label" htmlFor="acquisition-view-name">视图名称</label><input id="acquisition-view-name" className="form-input" autoFocus value={viewDialog.value} maxLength={30} placeholder="例如：核心获客指标" onChange={event => setViewDialog(current => current ? { ...current, value: event.target.value } : current)} onKeyDown={event => { if (event.key === 'Enter') saveView(); }} /></div><div className="modal-footer"><button type="button" className="btn btn-outline btn-sm" onClick={() => setViewDialog(null)}>取消</button><button type="button" className="btn btn-primary btn-sm" onClick={saveView}>保存</button></div></div></div>}
  </div>;
}
